<template>
  <div class="space-y-3">
    <div
      v-for="monitor in monitors" :key="monitor.id"
      class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
      <div class="flex items-start justify-between gap-4">
        <div class="min-w-0">
          <div class="flex items-center gap-2">
            <h4 class="font-medium text-gray-900 dark:text-gray-100 truncate">
              {{ monitor.name }}
            </h4>
            <span
              class="px-1.5 py-0.5 rounded text-xs"
              :class="monitor.enabled ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'">
              {{ monitor.enabled ? 'Active' : 'Paused' }}
            </span>
            <span
              v-if="monitor.new_findings_count > 0"
              class="px-1.5 py-0.5 rounded text-xs bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300">
              {{ monitor.new_findings_count }} new
            </span>
          </div>
          <p class="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            {{ monitor.metro }} · {{ frequencyLabel(monitor.frequency) }} ·
            {{ monitor.topics.length }} topic{{ monitor.topics.length === 1 ? '' : 's' }}
            <span v-if="monitor.slack_webhook_url"> · <i class="fab fa-slack" /> Slack</span>
          </p>
          <p class="text-xs text-gray-400 mt-1">
            <template v-if="runningMonitorIds[monitor.id]">
              <i class="far fa-spinner fa-spin mr-1" />
              Scanning… {{ runningMonitorIds[monitor.id].completed || 0 }}/{{ runningMonitorIds[monitor.id].total || monitor.topics.length }} topics
            </template>
            <template v-else-if="monitor.latest_run">
              Last run: <span class="capitalize" :class="{ 'text-red-500': monitor.latest_run.status === 'failed' }">{{ monitor.latest_run.status }}</span>
              <span v-if="monitor.latest_run.finished_at"> · {{ formatDate(monitor.latest_run.finished_at) }}</span>
              <span v-if="monitor.latest_run.status === 'completed'"> · {{ monitor.latest_run.new_findings_count }} new findings</span>
              <span v-if="monitor.latest_run.total_cost_cents"> · ~{{ formatCost(monitor.latest_run.total_cost_cents) }}</span>
              <span v-if="monitor.latest_run.status === 'failed' && monitor.latest_run.error_message" class="text-red-400">
                · {{ firstLine(monitor.latest_run.error_message) }}
              </span>
            </template>
            <template v-else>
              Never run
            </template>
          </p>
        </div>
        <div class="flex items-center gap-1 shrink-0">
          <button
            class="btn btn-primary btn-sm"
            :disabled="!!runningMonitorIds[monitor.id]"
            v-tooltip="'Run this monitor now instead of waiting for the schedule.'"
            @click="$emit('run-now', monitor)">
            <i class="far fa-play mr-1" />
            Run Now
          </button>
          <button class="btn btn-secondary btn-sm" @click="$emit('edit', monitor)">
            Edit
          </button>
          <button
            class="btn btn-secondary btn-sm text-red-600 dark:text-red-400"
            @click="$emit('destroy', monitor)">
            <i class="far fa-trash" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import vTooltip from '~/javascript/directives/tooltip'

defineProps({
  monitors: { type: Array, required: true },
  runningMonitorIds: { type: Object, default: () => ({}) }
})

defineEmits(['edit', 'destroy', 'run-now'])

const frequencyLabels = { daily: 'Daily', every_3_days: 'Every 3 days', weekly: 'Weekly' }
const frequencyLabel = (frequency) => frequencyLabels[frequency] || frequency
const formatDate = (iso) => new Date(iso).toLocaleString()
const formatCost = (cents) => `$${(cents / 100).toFixed(2)}`
const firstLine = (text) => text.split('\n')[0].slice(0, 120)
</script>
